import { FC, useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';

import { message, Spin } from 'antd';

import { CenteredLoaderWithBlur, Container } from '@//components/common';
import { leads } from '@/api/amo-crm/leads';
import { useDocsList } from '@/api/docs';
import { Contacts } from '@/components/ContactUs/Contacts';
import { Form } from '@/components/ContactUs/Form';
import { FormResults } from '@/components/ContactUs/types';
import { Environment } from '@/constants/environments';
import { TranslationNamespace } from '@/i18n';
import { getCurrentSessionUtm } from '@/utils';
import { getMappingDocs } from '@/utils/docs';

import { FishBgLarge, FishBgSmall } from './icons';
import { getSourceForContactUsCRM } from './utils';

import styles from './ContactUsForm.module.scss';

export const ContactUs: FC = () => {
	const [loading, setLoading] = useState(false);
	const t = useTranslation(TranslationNamespace.Marketing).t;
	const router = useRouter();
	const { locale } = router;

	const { data: docs, isLoading } = useDocsList(getMappingDocs);

	useEffect(() => {
		if (router.asPath.includes('#openoffice')) {
			const el = document.querySelector('#openoffice');
			el?.scrollIntoView({ block: 'start' });
		}
	}, []);

	const handleSubmit = async ({ name, phone }: FormResults) => {
		setLoading(true);
		try {
			const utm = getCurrentSessionUtm(window.location.href);
			const errors = await leads({
				phone,
				name,
				source: getSourceForContactUsCRM(process.env.ENVIRONMENT as Environment),
				...utm,
			});

			if (errors) {
				let text = '';
				Object.values(errors).forEach((v) => {
					text += `${v.join('\n')}\n`;
				});

				message.error(text);
				return;
			}
			message.success(t('contactus.request-sent'));
		} catch {
			message.error(t('contactus.request-error'));
		} finally {
			setLoading(false);
		}
	};

	if (isLoading) {
		return <CenteredLoaderWithBlur isLoading={isLoading} />;
	}

	const { privacyPolicy, userAgreement } = docs;

	return (
		<section className={styles.contactUsForm}>
			<Container>
				<div className={styles.container}>
					<div className={styles.contactUsFormInner}>
						<FishBgLarge />
						<FishBgSmall />

						<Contacts locale={locale} translate={t} />
						<Spin spinning={loading} size="large">
							<Form
								loading={loading}
								onSubmit={handleSubmit}
								translate={t}
								privacyPolicyLink={privacyPolicy}
								userAgreementLink={userAgreement}
							/>
						</Spin>
					</div>
				</div>
			</Container>
		</section>
	);
};
